import React from "react";
import {
    textVariantPropsKeys,
    textVariants,
} from "@singlestore/fusion/components/typography";
import { split } from "@singlestore/fusion/utils/object";
import { cva, VariantProps } from "class-variance-authority";
import { Link as GatsbyLink } from "gatsby";

import "./Link.scss";

export const link = cva("link", {
    variants: {
        ...textVariants,
        styled: {
            true: "styled",
        },
        type: {
            primary: "primary",
            secondary: "secondary",
        },
    },
});

// From Gatsby's internals: https://github.com/gatsbyjs/gatsby/blob/2975c4d1271e3da52b531ad2f49261c362e5ae13/packages/gatsby-link/src/index.js#L42-L46.
const isInternalLink = (path: string) =>
    path &&
    !path.startsWith(`http://`) &&
    !path.startsWith(`https://`) &&
    !path.startsWith(`//`);

const isIdLink = (path: string) => path && path.startsWith("#");

export type LinkProps = React.ComponentPropsWithoutRef<typeof GatsbyLink> &
    VariantProps<typeof link>;

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
    (
        {
            className,
            target: _target,
            rel,
            to,
            type = "primary",
            styled = true,
            ...rest
        },
        forwardedRef
    ) => {
        const [variantProps, linkProps] = split(rest, textVariantPropsKeys);

        const _className = link({
            ...variantProps,
            styled,
            type: styled ? type : null,
            className,
        });

        const isId = isIdLink(to);

        if (isInternalLink(to) && !isId) {
            return (
                <GatsbyLink
                    // https://github.com/gatsbyjs/gatsby/issues/34325
                    ref={forwardedRef as React.RefObject<any>}
                    to={to}
                    className={_className}
                    target={_target}
                    rel={rel}
                    {...linkProps}
                />
            );
        }

        const target = _target || (isId ? "_self" : "_blank");

        return (
            <a
                ref={forwardedRef}
                href={to}
                className={_className}
                target={target}
                rel={rel || "noopener noreferrer"}
                {...linkProps}
            />
        );
    }
);

Link.displayName = "Link";
